import { Auth } from "@aws-amplify/auth"
import { API } from "@aws-amplify/api"
import { retrieveSignature, storeSignature, getCurrentTokenType } from "../signatureLib/signatureLib"
import { BrowserStorageCache } from "@aws-amplify/cache"
import { refreshAraAccessToken } from "../signatureLib/refreshLib"
import { safeReload } from "../safeReloadLib/safeReloadLib"

export type Method = "get" | "post" | "put" | "patch" | "del" | "head"

export type QSParams = { [param: string]: string }
export type MVQSParams = { [param: string]: string[] }

const ttConfig = {
	defaultTTL: 1000 * 60 * 10,
	storage: window.sessionStorage,
	keyPrefix: "apiCache",
}

let ttCache

export function getTTCache() {
	if (!ttCache) {
		ttCache = BrowserStorageCache.createInstance(ttConfig)
	}
	return ttCache
}

export function indexArray(array, indexName, defaultVal = "-") {
	//take array of object and return obj with values given by indexName
	return array.reduce((acc, item) => {
		acc[item[indexName] || defaultVal] = item
		return acc
	}, {})
}

interface CognitoUserSession {
	idToken: {
		jwtToken: string
		payload: {
			sub: string
		}
	}
	accessToken: {
		jwtToken: string
	}
}

export async function getCognitoToken(sessionOverride?: CognitoUserSession): Promise<AuthHeaders> {
	try {
		const session = sessionOverride ?? ((await Auth.currentSession()) as unknown as CognitoUserSession)
		if (
			window.location.href?.includes("beta.hireara.ai") &&
			session?.idToken?.payload?.["custom:userType"] !== "beta"
		) {
			console.log("no beta access")
			alert("You have not been granted access to the beta site. Please contact support if you think this is an error.")
			await Auth.signOut()
			return null
		}
		return { Authorization: session?.idToken?.jwtToken, "x-access-token": session?.accessToken?.jwtToken }
	} catch (e) {
		if (e === "No current user") {
			await Auth.signOut()
			return null
		} else {
			throw e
		}
	}
}

async function getSignatureBasedToken(): Promise<AuthHeaders | null> {
	let sig = retrieveSignature()
	if (sig == null) {
		console.log("no token, refreshing")
		//attempt to refresh token
		try {
			const newSig = (await refreshAraAccessToken())?.accessToken
			if (newSig) {
				//successfully refreshed store and continue
				sig = newSig
				storeSignature(sig)
			}
		} catch (e) {
			console.log("token refresh failed", e)
			return null
		}
	}
	if (sig != null) {
		return {
			Authorization: sig,
			"x-access-token": "none",
		}
	}
	return null
}

export function applyMappingRecursive(mapping, data) {
	if (mapping == null) return mapping
	return Object.entries(mapping).reduce((acc, [key, value]) => {
		if (typeof value === "string") {
			acc[key] = data[value]
		} else {
			acc[key] = applyMappingRecursive(value, data)
		}
		return acc
	}, {})
}

export function createParamsFromMapping(mapping, data, extra = {}) {
	const params = applyMappingRecursive(mapping, data)
	for (let key in extra) {
		params[key] = { ...params[key], ...extra[key] }
	}
	return params
}

type AuthHeaders = { Authorization: string; "x-access-token"?: string }

export async function createSecurityParameters(inputs: {
	sessionOverride?: CognitoUserSession
}): Promise<{ headers: AuthHeaders }> {
	// normal user based auth
	const tokenType = getCurrentTokenType()
	let headers: AuthHeaders | null =
		tokenType === "signature" ? await getSignatureBasedToken() : await getCognitoToken(inputs?.sessionOverride)
	if (headers != null) {
		return { headers }
	}
	// no access, if previously logged in logout was called. Refresh to ensure screen cleared
	console.log("reloading due to lack of access")
	safeReload("noUser")
}

export function createApiFunction<OutputType>({
	type,
	path,
	idForPath = "clientId",
	mapping,
	defaultValues = {},
	inputTransform = (x) => x,
	outputTransform = (x) => x,
	errorHandler = (e) => {
		throw e
	},
	condition = () => true,
	shouldUseSecurity = true,
}: EndpointData) {
	return async function apiFunction(inputs: object): Promise<OutputType> {
		condition(inputs)
		const parameters = await createParameters(shouldUseSecurity, mapping, defaultValues, inputs, inputTransform)

		try {
			const fullPath = addIdsToPath(path, inputs?.[idForPath])
			const response = await API[type]?.(global.config.apiGateway.NAME, fullPath, parameters)
			const output = outputTransform(response)
			return output
		} catch (e) {
			return errorHandler(e)
		}
	}
}

const ID_DEPTHS = {
	"/folders": 2,
	"/applications": 3,
	"/links": 4,
	"/views": 5,
	"/output": 4,
	"/logos": 1,
	"/files": 4,
	"/users": 2,
	"/signatures": 1,
	"/jobs": 2,
	"/text-transform": 4,
	"/templates": 2,
	"/clients": 1,
	"/forwarding": 1,
	"/oauth": 1,
}

export function addIdsToPath(path: string, id?: string) {
	const idDepth = ID_DEPTHS[path] ?? 1
	if (id == null) {
		return path
	}
	// if (!id.includes("_")) {
	// 	return "/" + id + path
	// }
	const idParts = id.split("_")
	if (idDepth === 1 && idParts.length === idDepth) {
		return "/" + idParts[0] + path
	}
	if (idParts.length === idDepth) {
		return "/" + idParts.slice(0, idParts.length - 1).join("/") + path + "/" + idParts[idParts.length - 1]
	} else if (idParts.length === idDepth - 1) {
		return "/" + id.replaceAll("_", "/") + path
	} else if (idParts.length < idDepth - 1) {
		const missingIdParts = idDepth - 1 - idParts.length
		const extraPath = Array(missingIdParts).fill("/xxxxxxxxxxxx").join("")
		return "/" + id.replaceAll("_", "/") + extraPath + path
	} else {
		throw new Error("Invalid ID for depth")
	}
}

export async function createParameters(shouldUseSecurity, mapping, defaultValues, inputs, inputTransform) {
	const extras = shouldUseSecurity ? await createSecurityParameters(inputs) : {}
	return inputTransform(createParamsFromMapping(mapping, { ...defaultValues, ...inputs }, extras))
}

type BaseApiEndpointSet = {
	path: string
}

type ApiEndpointFunctionSet<T extends string> = {
	[functionName in T]: (inputs: object) => Promise<object>
}

type ApiEndpointSet<T extends string> = BaseApiEndpointSet & ApiEndpointFunctionSet<T>

type EndpointData = {
	type: Method
	path?: string
	idForPath?: string
	mapping: object
	defaultValues?: object
	inputTransform?: (x: InputType) => object
	outputTransform?: (x: InputType) => any
	errorHandler?: (e: { response?: { status?: number }; message?: string }) => any
	condition?: (x: InputType) => boolean
	shouldUseSecurity?: boolean
}

type InputType = {
	[key: string]: any
}

export function makeApiEndpointSet<T extends string>(
	endpointPath: string,
	endpointData: { [endpoint in T]: EndpointData }
): ApiEndpointSet<T> {
	let out: ApiEndpointSet<T> = {} as ApiEndpointSet<T>
	out.path = endpointPath
	for (let [name, data] of Object.entries<EndpointData>(endpointData)) {
		out[name] = createApiFunction({
			...data,
			path: endpointPath,
		})
	}

	return out
}

export function combinePreviousText(previousText, currentText) {
	if (previousText == null || previousText.length === 0) {
		return currentText
	}
	const lastPrevChar = previousText[previousText.length - 1]
	const firstCurrentChar = currentText[0]
	// rules:
	// both chars capital or numbers or symbols: no space
	// either char is ': no space
	// first char is symbol: no space
	// either char is a space
	const lastPrevCharUpperOrNumber = lastPrevChar === lastPrevChar.toUpperCase()
	const firstCurrentCharUpperOrNumber = firstCurrentChar === firstCurrentChar.toUpperCase()

	const excludeSpace =
		(lastPrevCharUpperOrNumber && firstCurrentCharUpperOrNumber && ![",", "."].includes(lastPrevChar)) ||
		lastPrevChar === "'" ||
		firstCurrentChar === "'" ||
		!firstCurrentChar.match(/[a-z0-9]/i) ||
		firstCurrentChar === " " ||
		lastPrevChar === " "

	return previousText + (excludeSpace ? "" : " ") + currentText
}

export function makeUrl(baseUrl, queryStringParameters: QSParams) {
	let qsString = Object.entries(queryStringParameters)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join("&")
	if (qsString.length > 0) {
		return `${baseUrl}?${qsString}`
	} else {
		return baseUrl
	}
}
